import type * as React from "react";
import { cn } from "#ui-atoms/lib/utils";
import type { OptionalTenantProp } from "../../../types";
import type { CardParts } from "./index";

type BaseCardProps = React.ComponentProps<"div"> & OptionalTenantProp;

function BaseCard({ className, ...props }: BaseCardProps) {
  return (
    <div
      className={cn(
        "relative flex flex-col gap-6 rounded-2xl border bg-card bg-clip-padding py-6 text-card-foreground shadow-xs before:pointer-events-none before:absolute before:inset-0 before:rounded-[calc(var(--radius-2xl)-1px)] before:shadow-[0_1px_--theme(--color-black/4%)] dark:bg-clip-border dark:before:shadow-[0_-1px_--theme(--color-white/8%)]",
        className,
      )}
      data-slot="card"
      {...props}
    />
  );
}

function BaseCardHeader({ className, ...props }: BaseCardProps) {
  return (
    <div
      className={cn(
        "@container/card-header grid auto-rows-min grid-rows-[auto_auto] items-start gap-1.5 px-6 has-data-[slot=card-action]:grid-cols-[1fr_auto] [.border-b]:pb-6",
        className,
      )}
      data-slot="card-header"
      {...props}
    />
  );
}

function BaseCardTitle({ className, ...props }: BaseCardProps) {
  return (
    <div
      className={cn("font-semibold text-lg leading-none", className)}
      data-slot="card-title"
      {...props}
    />
  );
}

function BaseCardDescription({ className, ...props }: BaseCardProps) {
  return (
    <div
      className={cn("text-muted-foreground text-sm", className)}
      data-slot="card-description"
      {...props}
    />
  );
}

function _BaseCardAction({
  className,
  ...props
}: Omit<BaseCardProps, "tenant">) {
  return (
    <div
      className={cn(
        "col-start-2 row-span-2 row-start-1 self-start justify-self-end",
        className,
      )}
      data-slot="card-action"
      {...props}
    />
  );
}

function BaseCardPanel({ className, ...props }: BaseCardProps) {
  return (
    <div
      className={cn("px-6", className)}
      data-slot="card-content"
      {...props}
    />
  );
}

function BaseCardFooter({ className, ...props }: BaseCardProps) {
  return (
    <div
      className={cn("flex items-center px-6 [.border-t]:pt-6", className)}
      data-slot="card-footer"
      {...props}
    />
  );
}

export {
  BaseCard,
  BaseCardHeader,
  BaseCardTitle,
  BaseCardDescription,
  BaseCardPanel,
  BaseCardFooter,
  type BaseCardProps,
};

export const baseCardKit: CardParts = {
  Root: BaseCard,
  Header: BaseCardHeader,
  Title: BaseCardTitle,
  Description: BaseCardDescription,
  Content: BaseCardPanel,
  Footer: BaseCardFooter,
};
